import { useAuth0 } from '@auth0/auth0-react';
import {
    faLocationDot,
    faPhoneFlip,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import React, {
    useCallback,
    useEffect,
    useState,
    useContext,
    useRef,
} from 'react';
import {
    AdSlot,
    DFPSlotsProvider,
    DFPManager,
} from 'react-dfp';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Descriptions } from '../../constants/Descriptions';
import * as routes from '../../constants/routes';
import {
    COLORS,
    ThemeContext,
    POL_THEME,
    MCO_THEME,
} from '../../constants/theme';
import { Titles } from '../../constants/titles';
import { AppContext } from '../../context/AppContext';
import customerDataRequest from '../../services/api/customer-data';
import { getIsMobile } from '../../utils/browserAgent';
import { formatPostalCode } from '../../utils/location';
import { loadContractsCallback } from '../../utils/organization';
import { fillRouteParameters } from '../../utils/routes';

import Loader from '../shared/Loader';

import styles from './SupportersContainer.scss';

const styleProps = {
    categorySections: {
        backgroundColor: '#F6F7FF',
        color: '#686868',
        border: '1px solid #EDEDED',
        borderRadius: '4px 4px 0px 0px',
        opacity: 1,
    },
    addressSection: {
        marginTop: '15px',
        marginLeft: '15px',
    },
    addressTitle: {
        font: 'normal normal normal 16px/18px Helvetica Neue',
        letterSpacing: '0px',
        color: '#008085',
        opacity: 1,
    },
    addressContents: {
        font: 'normal normal normal 14px/20px "Open Sans"',
        color: '#645F5F',
        letterSpacing: '0px',
    },
    phoneIcon: {
        paddingTop: '15px',
        paddingRight: '10px',
        marginLeft: '-18px',
    },
};

// BreadCrumbs title for supporters
const breadCrumbsTitle = (slug, physicalTitle) => (
    <Breadcrumbs aria-label="breadcrumb">
        <Link
            underline="hover"
            color="inherit"
            to={fillRouteParameters(routes.ORGANIZATION_DETAIL, { slug })}
        >
            {physicalTitle}
        </Link>
        <div>
            Supporters
        </div>
    </Breadcrumbs>
);

const sponsorDetails = (sponsorInfo) => (
    <div key={sponsorInfo.advertiser_id}>
        <Link
            to={fillRouteParameters(routes.SPONSOR_DETAIL, { slug: sponsorInfo.slug })}
            style={styleProps.addressTitle}
        >
            {sponsorInfo.physical_title}
        </Link>
        <div style={styleProps.addressSection}>

            <div style={styleProps.addressContents}>
                <FontAwesomeIcon
                    icon={faLocationDot}
                    style={{ paddingRight: '10px', marginLeft: '-18px' }}
                />
                {sponsorInfo.physical_street}
                <br />
                {sponsorInfo.physical_street2 && (
                    <>
                        {sponsorInfo.physical_street2}
                        <br />
                    </>
                )}
                {sponsorInfo.physical_street3 && (
                    <>
                        {sponsorInfo.physical_street3}
                        <br />
                    </>
                )}
                {sponsorInfo.physical_city}
                &#44;&nbsp;
                {sponsorInfo.physical_state}
                &nbsp;
                {formatPostalCode(sponsorInfo.physical_postal_code)}
            </div>

            <a href={`tel:${sponsorInfo.physical_phone}`}>
                <FontAwesomeIcon
                    icon={faPhoneFlip}
                    style={styleProps.phoneIcon}
                />
                {sponsorInfo.physical_phone}
            </a>
        </div>

    </div>
);

// Supporters sorting within each category in alphabetical order
const sortSupportersCategory = (supportersCategory) => supportersCategory
    .sort((a, b) => ((a.physical_title > b.physical_title) ? 1 : -1));

const icon = (iconKey) => <FontAwesomeIcon icon={['far', iconKey]} />;

const supportersCategorySection = (sponsorGroups) => {
    const sortedSponsorGroups = Object.keys(sponsorGroups).sort();

    return (
        // order keys alphabetically
        sortedSponsorGroups.map((spon) => (
            <Card
                variant="outlined"
                key={`${spon}-section`}
            >
                <CardHeader
                    key={`${spon}-category`}
                    sx={styleProps.categorySections}
                    avatar={icon(sponsorGroups[spon][0].sic_icon)}
                    title={spon}
                />
                <CardContent>
                    {sortSupportersCategory(sponsorGroups[spon]).map((sponsorInfo, index) => {
                        const supportersLength = sponsorGroups[spon].length;
                        const isLastSupporter = index === (supportersLength - 1);
                        const isDividerNeeded = (supportersLength > 1 && !isLastSupporter);
                        return (
                            <React.Fragment key={`${spon}-contents-${sponsorInfo.advertiser_id}`}>
                                {sponsorDetails(sponsorInfo)}
                                {isDividerNeeded
                                    && (
                                        <Divider
                                            sx={{
                                                my: '15px',
                                            }}
                                            key={`${spon}-divider`}
                                        />
                                    )}
                            </React.Fragment>
                        );
                    })}

                </CardContent>
            </Card>
        ))
    );
};

const supportersMainSection = (sponsorsGroup) => (
    <div className={styles.contentContainer}>
        <div className={styles.sponsorCategoryContainer}>
            <Divider />
            {supportersCategorySection(sponsorsGroup)}
        </div>

    </div>
);

const supportersNoFoundSection = () => (
    <div>
        <p>
            Supporters are not available for current selection
        </p>
    </div>
);

const SupportersPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [sponsorsGroup, setSponsorsGroup] = useState({});
    const [orgPhysicalTitle, setOrgPhysicalTitle] = useState('');
    const [organizationContractIds, setOrganizationContractIds] = useState(undefined);
    const [organization, setOrganization] = useState({});
    const { salesforce_id: salesforceId } = organization || {};
    const [dataReady, setDataReady] = useState({ org: false, contracts: false });
    const adSetupComplete = useRef(false);
    const { slug } = useParams();
    const themeContext = useContext(ThemeContext);
    const { setPageTitle, setPageDescription } = useContext(AppContext);

    const isMobile = getIsMobile();

    const rightColumnAdSlots = {
        POL: [
            {
                unit: 'SAF2_Listing_Sponsor_300x600',
                sizes: [[300, 600]],
            },
            {
                unit: 'SAF2_OrgListing1_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'SAF2_OrgListing2_300x100',
                sizes: [[300, 100]],
            },
            {
                unit: 'SAF2_OrgListing3_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'SAF2_OrgListing4_300x100',
                sizes: [[300, 100]],
            },
        ],
        MCO: [
            {
                unit: 'OSC2_ListingSponsor_300x600',
                sizes: [[300, 600]],
            },
            {
                unit: 'OSC2_OrgListing1_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'OSC2_OrgListing2_300x100',
                sizes: [[300, 100]],
            },
            {
                unit: 'OSC2_OrgListing3_300x250',
                sizes: [[300, 250]],
            },
            {
                unit: 'OSC2_OrgListing4_300x100',
                sizes: [[300, 100]],
            },
        ],
    };

    const getSponsors = useCallback(async () => {
        if (slug) {
            setIsLoading(true);
            let sicDataList = [];
            let newOrganization = {};
            try {
                sicDataList = get(await customerDataRequest('get', '/sponsors/sic-codes'), 'data.data', null);
                const orgResp = await customerDataRequest('get', `/organizations/slug/${slug}/`);
                newOrganization = orgResp.data.data;
                setOrganization(newOrganization);
                setOrgPhysicalTitle(newOrganization.physical_title);
            } catch (error) {
                toast.error('There was an error getting Organization or Sic data. Please try again in a few minutes');
            }

            await customerDataRequest('get', `organizations/${newOrganization.salesforce_id}/sponsors`).then((res) => {
                const sponsorsData = res.data.data.map((sponsor) => {
                    const mappedSponsors = sponsor;
                    // find sic record by sic_code
                    const sicObjectData = sicDataList.find((sic) => sic.sic_code === mappedSponsors.sic_code);
                    mappedSponsors.sic_name = sicObjectData.name;
                    mappedSponsors.sic_icon = sicObjectData.sic_icon;
                    return mappedSponsors;
                });
                const sponsorsByGroup = groupBy(sponsorsData, 'sic_name');
                setSponsorsGroup(sponsorsByGroup);
                setIsLoading(false);
            })
                .catch(() => {
                    setIsLoading(false);
                    toast.error('There was an error getting sponsors data. Please try again in a few minutes. ');
                });
        }
    }, [slug]);

    useEffect(() => {
        getSponsors();
    }, [getSponsors]);

    useEffect(() => {
        if (organization && organization.salesforce_id) {
            setDataReady((prev) => ({ ...prev, org: true }));
        }
    }, [organization]);

    const {
        getAccessTokenSilently,
    } = useAuth0();

    const loadContracts = useCallback(() => {
        if (salesforceId) {
            loadContractsCallback(salesforceId, getAccessTokenSilently, () => null, setOrganizationContractIds);
        }
    }, [getAccessTokenSilently, salesforceId]);

    useEffect(() => {
        loadContracts();
    }, [loadContracts]);

    useEffect(() => {
        if (organizationContractIds || organizationContractIds === null) {
            setDataReady((prev) => ({ ...prev, contracts: true }));
        }
    }, [organizationContractIds]);

    useEffect(() => {
        if (dataReady.org && dataReady.contracts && !adSetupComplete.current) {
            DFPManager.setTargetingArguments({
                Church_ID: organizationContractIds,
                Salesforce_Id: salesforceId,
                City: organization.physical_city,
                State: organization.physical_state,
                Zip: organization.physical_postal_code,
                Category: organization.tags,
            });
            DFPManager.refresh();
            adSetupComplete.current = true;
        }
    }, [dataReady, organization, organizationContractIds, salesforceId]);

    useEffect(() => {
        if (orgPhysicalTitle) {
            switch (themeContext) {
                case MCO_THEME:
                    setPageTitle(Titles.MCO_SUPPORTERS_LIST(orgPhysicalTitle));
                    setPageDescription(Descriptions.MCO_SUPPORTERS_LIST(orgPhysicalTitle));
                    break;
                case POL_THEME:
                    setPageTitle(Titles.POL_SUPPORTERS_LIST(orgPhysicalTitle));
                    setPageDescription(Descriptions.POL_SUPPORTERS_LIST(orgPhysicalTitle));
                    break;
                default:
                    break;
            }
        }
    }, [orgPhysicalTitle, themeContext, setPageTitle, setPageDescription]);

    return (
        <>
            {isLoading && <Loader color={COLORS.secondary} />}
            <DFPSlotsProvider
                dfpNetworkId={process.env.DFP_NETWORK_ID}
                singleRequest
                disableInitialLoad
            >
                <main className={styles.container}>
                    {!isLoading
                        && (
                            <div className={styles.backgroundContainer}>
                                <h1
                                    className={styles.sponsorsBreadCrumbsContainer}
                                    aria-label={`${orgPhysicalTitle} Supporters`}
                                >
                                    {breadCrumbsTitle(slug, orgPhysicalTitle)}
                                </h1>
                                {sponsorsGroup
                                    ? supportersMainSection(sponsorsGroup)
                                    : supportersNoFoundSection()}
                            </div>
                        )}
                    <div className={styles.adContainer}>
                        <div className={styles.rightColumnAdContainer}>
                            {themeContext === POL_THEME && !isMobile
                                && rightColumnAdSlots.POL.map((ad, index) => (
                                    <div
                                        className={styles.adSlotContainer}
                                        key={ad.unit}
                                    >
                                        <AdSlot
                                            dfpNetworkId={process.env.DFP_NETWORK_ID}
                                            adUnit={ad.unit}
                                            sizes={ad.sizes}
                                            slotId={`right-column-ad-${index}`}
                                        />
                                    </div>
                                ))}
                            {themeContext === MCO_THEME && !isMobile
                                && rightColumnAdSlots.MCO.map((ad, index) => (
                                    <div
                                        className={styles.adSlotContainer}
                                        key={ad.unit}
                                    >
                                        <AdSlot
                                            dfpNetworkId={process.env.DFP_NETWORK_ID}
                                            adUnit={ad.unit}
                                            sizes={ad.sizes}
                                            slotId={`right-column-ad-${index}`}
                                        />
                                    </div>
                                ))}
                        </div>
                    </div>
                </main>
            </DFPSlotsProvider>
        </>
    );
};

export default SupportersPage;
